import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { Globe, Plus, X, Save, AlertCircle, ExternalLink } from 'lucide-react';

interface DomainPattern {
  pattern: string;
  label?: string;
  isPrimary?: boolean;
}

interface DomainSettingsProps {
  projectId: string;
  initialDomains: DomainPattern[];
  onSave?: () => void;
}

export function DomainSettings({ projectId, initialDomains, onSave }: DomainSettingsProps) {
  const queryClient = useQueryClient();
  const [domains, setDomains] = useState<DomainPattern[]>(initialDomains || []);
  const [newPattern, setNewPattern] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  const updateMutation = useMutation({
    mutationFn: async (allowedDomains: DomainPattern[]) => {
      return api.patch(`/projects/${projectId}`, {
        settings: { allowedDomains }
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
      setHasChanges(false);
      onSave?.();
    },
    onError: (err: any) => {
      setError(err.message || 'Failed to save settings');
    }
  });

  const validatePattern = (pattern: string): { isValid: boolean; error?: string } => {
    if (!pattern || pattern.trim() === '') {
      return { isValid: false, error: 'Pattern cannot be empty' };
    }
    
    const cleaned = pattern.trim().toLowerCase();
    
    if (cleaned.includes('://')) {
      return { isValid: false, error: 'Do not include protocol (http/https)' };
    }
    
    if (cleaned.includes('/')) {
      return { isValid: false, error: 'Do not include path, only domain' };
    }
    
    const domainPart = cleaned.replace(/^\*[\.-]?/, '');
    const domainRegex = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)*$/;
    
    if (!domainRegex.test(domainPart)) {
      return { isValid: false, error: 'Invalid domain format' };
    }
    
    return { isValid: true };
  };

  const handleAddDomain = () => {
    const pattern = newPattern.trim().toLowerCase();
    
    const validation = validatePattern(pattern);
    if (!validation.isValid) {
      setError(validation.error || 'Invalid pattern');
      return;
    }

    if (domains.some(d => d.pattern === pattern)) {
      setError('This domain pattern already exists');
      return;
    }

    const newDomain: DomainPattern = {
      pattern,
      label: newLabel.trim() || undefined,
      isPrimary: domains.length === 0 // First domain is primary
    };

    setDomains([...domains, newDomain]);
    setNewPattern('');
    setNewLabel('');
    setError(null);
    setHasChanges(true);
  };

  const handleRemoveDomain = (index: number) => {
    const updated = domains.filter((_, i) => i !== index);
    // If we removed the primary, make first one primary
    if (domains[index].isPrimary && updated.length > 0) {
      updated[0].isPrimary = true;
    }
    setDomains(updated);
    setHasChanges(true);
  };

  const handleSetPrimary = (index: number) => {
    const updated = domains.map((d, i) => ({
      ...d,
      isPrimary: i === index
    }));
    setDomains(updated);
    setHasChanges(true);
  };

  const handleSave = () => {
    setError(null);
    updateMutation.mutate(domains);
  };

  const getNavigableUrl = (pattern: string): string => {
    let domain = pattern;
    if (domain.startsWith('*.')) {
      domain = domain.slice(2);
    } else if (domain.startsWith('*-')) {
      domain = domain.slice(2);
    } else if (domain.includes('*')) {
      domain = domain.replace(/\*/g, '');
    }
    return `https://${domain}`;
  };

  return (
    <div className="card">
      <div className="card-header">
        <div className="w-8 h-8 rounded-md flex items-center justify-center"
          style={{ background: 'var(--warning-dim)' }}
        >
          <Globe className="w-4 h-4" style={{ color: 'var(--warning)' }} />
        </div>
        <h3 className="card-title">Domain Restrictions</h3>
      </div>
      
      <p className="text-sm text-muted mb-4">
        Restrict the browser extension to work only on specific domains. 
        If no domains are specified, the extension works on all pages.
      </p>

      {error && (
        <div className="flex items-center gap-2 p-3 mb-4 rounded-lg text-sm"
          style={{ background: 'var(--error-dim)', color: 'var(--error)' }}
        >
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Existing domains */}
      {domains.length > 0 && (
        <div className="space-y-2 mb-4">
          {domains.map((domain, index) => (
            <div 
              key={index}
              className="flex items-center gap-3 p-3 rounded-lg group"
              style={{ background: 'var(--surface-2)' }}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <code className="text-sm font-mono text-default">
                    {domain.pattern}
                  </code>
                  {domain.isPrimary && (
                    <span className="badge badge-success text-xs">Primary</span>
                  )}
                </div>
                {domain.label && (
                  <p className="text-xs text-muted mt-1">{domain.label}</p>
                )}
              </div>
              <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <a 
                  href={getNavigableUrl(domain.pattern)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-1.5 rounded hover:bg-surface-3 transition-colors"
                  title="Open in new tab"
                >
                  <ExternalLink className="w-4 h-4 text-muted" />
                </a>
                {!domain.isPrimary && (
                  <button
                    onClick={() => handleSetPrimary(index)}
                    className="text-xs text-muted hover:text-default transition-colors"
                    title="Set as primary"
                  >
                    Set primary
                  </button>
                )}
                <button
                  onClick={() => handleRemoveDomain(index)}
                  className="p-1.5 rounded hover:bg-error-dim transition-colors"
                  title="Remove"
                >
                  <X className="w-4 h-4 text-error" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add new domain */}
      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <input
          type="text"
          value={newPattern}
          onChange={(e) => {
            setNewPattern(e.target.value);
            setError(null);
          }}
          placeholder="example.com, *.example.com, *-preview.app"
          className="input flex-1"
          onKeyDown={(e) => e.key === 'Enter' && handleAddDomain()}
        />
        <input
          type="text"
          value={newLabel}
          onChange={(e) => setNewLabel(e.target.value)}
          placeholder="Label (optional)"
          className="input w-full sm:w-40"
          onKeyDown={(e) => e.key === 'Enter' && handleAddDomain()}
        />
        <button
          onClick={handleAddDomain}
          className="btn btn-secondary"
          disabled={!newPattern.trim()}
        >
          <Plus className="w-4 h-4" />
          Add
        </button>
      </div>

      {/* Pattern examples */}
      <div className="p-3 rounded-lg text-xs mb-4" style={{ background: 'var(--surface-2)' }}>
        <p className="font-medium text-default mb-2">Pattern examples:</p>
        <ul className="space-y-1 text-muted">
          <li><code className="text-primary">app.example.com</code> — exact domain match</li>
          <li><code className="text-primary">*.example.com</code> — any subdomain (dev, staging, etc)</li>
          <li><code className="text-primary">*-example.pages.dev</code> — preview deploys with prefixes</li>
        </ul>
      </div>

      {/* Save button */}
      {hasChanges && (
        <div className="flex justify-end">
          <button
            onClick={handleSave}
            className="btn btn-primary"
            disabled={updateMutation.isPending}
          >
            {updateMutation.isPending ? (
              <>
                <span className="loading-spinner w-4 h-4" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Save Changes
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
}

